'use client';
import React from 'react';

import { useAppDispatch } from '@/lib/hooks';
import { setisEditingCourseData } from '@/lib/slices/course-slice';

import ContentEditableWrapper from '@/common/components/ContentEditableWrapper';

import { CourseBuilderData } from 'course-types';

interface Props {
  courseData: CourseBuilderData;
  onChange: (field: string, value: string, unitIndex?: number, subunitIndex?: number) => void;
}

const ReviewStep: React.FC<Props> = ({ courseData, onChange }) => {
  const dispatch = useAppDispatch();

  const handleEdit = (
    field: string,
    value: string,
    unitIndex?: number,
    subunitIndex?: number,
  ): void => {
    if (value) {
      onChange(field, value.trim(), unitIndex, subunitIndex);
      dispatch(setisEditingCourseData(false));
    }
  };

  const handleFocus = (): void => {
    dispatch(setisEditingCourseData(true));
  };

  return (
    <div className='flex flex-col h-full gap-1'>
      <div className='flex flex-col gap-1 bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
        <p className='text-base'>Review your course structure.</p>
        <p className='text-sm text-text_gray_900'>
          Click on any text to edit it directly. All changes will be saved automatically.
        </p>
      </div>

      <div id='reviewContainer' className='flex-1 overflow-y-auto min-h-[200px] max-h-[35vh]'>
        {/* Course Name */}
        <div className='mb-2'>
          <div className='bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
            <h3 className='text-sm text-text_gray_900 mb-1'>Course Name</h3>
            <ContentEditableWrapper
              handleInput={(content) => handleEdit('name', content)}
              handleFocus={handleFocus}
              textStyles='text-base font-semibold'
            >
              {courseData.name}
            </ContentEditableWrapper>
          </div>
        </div>

        {/* Course Image */}
        {courseData.image && (
          <div className='mb-2'>
            <div className='bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
              <h3 className='text-sm text-text_gray_900 mb-1'>Course Image</h3>
              <img
                src={URL.createObjectURL(courseData.image)}
                alt={courseData.name}
                className='w-full h-auto rounded-lg'
              />
            </div>
          </div>
        )}

        {/* Units and Subunits */}
        {courseData.units.map((unit, unitIndex) => (
          <div key={`unit-${unitIndex}`} className='mb-2 last:mb-0'>
            <div className='bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
              <div className='flex flex-col gap-1'>
                <p className='text-sm text-text_gray_900'>Unit {unitIndex + 1}</p>
                <ContentEditableWrapper
                  handleInput={(content) => handleEdit('name', content, unitIndex)}
                  handleFocus={handleFocus}
                  textStyles='text-base font-semibold'
                >
                  {unit.name}
                </ContentEditableWrapper>
              </div>

              {unit.description && (
                <ContentEditableWrapper
                  handleInput={(content) => handleEdit('description', content, unitIndex)}
                  handleFocus={handleFocus}
                  textStyles='text-sm text-text_gray_900 mt-1'
                >
                  {unit.description}
                </ContentEditableWrapper>
              )}

              <div className='mt-2 pl-3 border-l border-border_gray_300'>
                <h4 className='text-sm text-text_gray_900 mb-1'>Subunits</h4>
                <div className='flex flex-col gap-1'>
                  {unit.subunits.map((subunit, subunitIndex) => (
                    <div
                      key={`subunit-${unitIndex}-${subunitIndex}`}
                      className='bg-surface_gray_50 rounded-lg p-2 border border-border_gray_300'
                    >
                      <ContentEditableWrapper
                        handleInput={(content) =>
                          handleEdit('name', content, unitIndex, subunitIndex)
                        }
                        handleFocus={handleFocus}
                        textStyles='text-base'
                      >
                        {subunit.name}
                      </ContentEditableWrapper>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='sticky bottom-0 bg-surface_gray_50 pt-1'>
        <p className='text-sm text-text_gray_900 italic'>
          Review all content carefully before publishing. Click &quot;Publish Course&quot; when
          ready.
        </p>
      </div>
    </div>
  );
};

export default ReviewStep;
